import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ChangeEvent, KeyboardEvent} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import type {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    PostSortRowsParams,
    SuppressHeaderKeyboardEventParams,
    SuppressKeyboardEventParams
} from 'ag-grid-community';
import {CircleQuestionMark, PencilIcon, Trash2Icon} from 'lucide-react';
import {AppGrid} from 'components/common/ag-grid/AppGrid.component';
import {useAvailableHeight} from 'components/common/ag-grid/useAvailableHeight';
import {Button, ButtonType} from 'components/common/Button.component';
import {TextInput} from 'components/common/TextInput.component';
import {ICON_SIZE, KEY} from 'components/common/constants';
import {Loader, LoaderSize} from 'components/common/Loader.component';
import {Tooltip} from 'components/tooltip/Tooltip';
import {HEADING_INFO_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {launchDarkly} from 'utils/launchDarkly';
import {LOADING_STATUS, MODAL_ACTIONS, MODAL_SEVERITY} from 'utils/constants';
import {handleError, insufficientRightsError} from 'utils/errorHandle.function';
import {userHasAccessRights} from 'utils/accessRights.function';
import * as Api from 'utils/api/api';
import type {Role, RoleGridRow} from 'types/roles';
import {canViewConfiguration} from '../menu/Menu.component';
import * as reduxSelectors from '../store/application.reducers';
import Roles from './Roles.component';
import './newRoles.scss';

// swaps the classic list in Roles.component for the ag-grid rendering below
const AG_GRID_IN_CONFIGURATION_FLAG = 'fe-use-ng-grid-in-configuration';

// the columns the grid draws, in the order the classic list had them
const NAME_COL = 'name';
const ACTIONS_COL = 'actions';

/**
 * Row id of the draft row the Add button opens. A role that does not exist yet has no id of its
 * own, and only one draft is ever open, so a constant is all it needs.
 */
const NEW_ROLE_ROW_ID = 'new-role';

// one line of text, with room for the ghost buttons beside it
const ROW_HEIGHT = 52;

// the grid fills the page, keeping below it the Add button (40px, and the 24px it stands off the
// grid), the card's own padding and the page's bottom margin
const GRID_HEIGHT = Object.freeze({bottomGap: 112, watchSelector: '.new-roles__card'});

// wide enough for the Save and Cancel buttons a row in edit mode wears
const ACTIONS_WIDTH = 180;

// the Add button, so focus can go back to it once a draft row is done with
const ADD_BUTTON_ID = 'roles-add-button';

// how many frames a row is given to be drawn again before focus gives up on it (see returnFocus)
const FOCUS_RENDER_ATTEMPTS = 5;

/** The Edit button of a row, so focus can go back to it once that row's editor closes. */
const editButtonId = (rowId: string) => `roles-edit-button-${rowId}`;

// the tab order below is these, in the order the page draws them. A disabled button is no stop,
// which is every button on the page a system viewer reads
const SORTABLE_HEADERS = '.ag-header-cell-sortable';
const ACTION_BUTTONS = '.new-roles__actions button:not(:disabled)';
// the input the row being edited puts in its name cell, ahead of that row's own buttons
const NAME_INPUT = '.new-roles__name-input .ga-input__field';
const ROW_CONTROLS = `${NAME_INPUT}, ${ACTION_BUTTONS}`;

/**
 * Takes Tab off the heading the tab order leaves the header row from - the name, the only one
 * that sorts and so the only one worth standing on. The grid would walk on to the actions
 * heading, which does nothing, and then loop back to the first, which is a ring focus never gets
 * out of; onGridKeyDown carries it into the rows instead. Shift-Tab is left alone - walking back
 * up the headings is what it should do.
 */
const tabOutOfTheHeaderIsNotTheGrids = ({event}: SuppressHeaderKeyboardEventParams<RoleGridRow>) =>
    event.key === KEY.TAB && !event.shiftKey;

/**
 * Keeps the draft row under the roles, wherever the sort would otherwise put it: its name is
 * empty, so sorting by name would carry it to whichever end the empty string belongs at.
 */
function keepDraftLast({nodes}: PostSortRowsParams<RoleGridRow>) {
    const draft = nodes.findIndex(node => node.data?.id === NEW_ROLE_ROW_ID);
    if (draft !== -1) {
        nodes.push(...nodes.splice(draft, 1));
    }
}

/**
 * The rows the grid draws: the loaded roles, and the draft under them - which is where the
 * classic list put its new row.
 */
function buildRowData(roles: Role[], showNewRow: boolean): RoleGridRow[] {
    const rows: RoleGridRow[] = roles.map(role => ({
        id: String(role.roleId),
        roleId: role.roleId,
        name: role.name
    }));

    return showNewRow ? [...rows, {id: NEW_ROLE_ROW_ID, roleId: null, name: ''}] : rows;
}

/**
 * ag-grid rendering of the Configuration -> Roles page, behind fe-use-ng-grid-in-configuration.
 * The classic list it replaces is in Roles.component.
 */
export const NewRoles = () => {

    const {translate} = useTranslation();
    const location = useLocation();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);

    const [roles, setRoles] = useState<Role[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<number>(LOADING_STATUS.LOADING);

    // the draft row, and the row whose name is open for editing - which is the draft while it is
    // being filled in. Only one row is ever in edit mode, as in the classic list
    const [showNewRow, setShowNewRow] = useState(false);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);

    /**
     * What is typed into the row being edited. A ref rather than state: a keystroke here must not
     * rebuild the column definitions, which is what would tear the input out from under the
     * cursor. The classic list's new row kept its value the same way.
     */
    const draftName = useRef('');

    // the loader stands in the grid's place until the roles land, and the two are measured as one
    // box, so the card keeps its height when the rows arrive. Whichever of them is mounted is the
    // element measured, which is why it arrives through a callback
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const gridApi = useRef<GridApi<RoleGridRow> | null>(null);
    const onGridReady = useCallback((event: GridReadyEvent<RoleGridRow>) => {
        gridApi.current = event.api;
    }, []);

    // a system viewer who is not also an administrator only ever reads this page
    const readOnly = !!userRoles?.systemViewer && !userRoles?.systemAdministrator;

    const getData = useCallback(() => {
        setLoadingStatus(LOADING_STATUS.LOADING);
        Api.getRuleConfigurationRoles(currentContextId).then((result: Role[]) => {
            setRoles(result || []);
            setShowNewRow(false);
            setEditingRowId(null);
            setLoadingStatus(LOADING_STATUS.DONE);
        }, (error: unknown) => {
            setLoadingStatus(LOADING_STATUS.ERROR);
            handleError(error);
        });
    }, [currentContextId]);

    // loads on mount, and starts over whenever the company context changes - as the wrapper the
    // classic page extended did
    useEffect(() => {
        getData();
    }, [getData]);

    // alone among the configuration pages, this one raises the rights error rather than quietly
    // serving what the user may not see - as the classic page did
    useEffect(() => {
        if (!userHasAccessRights(userRoles, location) || !canViewConfiguration(userRoles)) {
            handleError(insufficientRightsError);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- checked once, as the classic page checked on mount

    /**
     * The row whose editor has just closed. Save and Cancel unmount themselves as they are
     * pressed, which would drop keyboard focus onto the document body - this is what says where
     * it should land instead.
     */
    const returnFocusTo = useRef<string | null>(null);

    /** Leaves edit mode, dropping the draft row with it. */
    const cancelEdit = useCallback(() => {
        returnFocusTo.current = editingRowId;
        draftName.current = '';
        setEditingRowId(null);
        setShowNewRow(false);
    }, [editingRowId]);

    const enterEditMode = useCallback((row: RoleGridRow) => {
        draftName.current = row.name;
        setEditingRowId(row.id);
        // opening a role for editing gives up an unsaved draft, since only one row can be edited
        setShowNewRow(false);
    }, []);

    const addRole = useCallback(() => {
        draftName.current = '';
        setShowNewRow(true);
        setEditingRowId(NEW_ROLE_ROW_ID);
    }, []);

    const saveRow = useCallback(() => {
        const name = draftName.current.trim();
        const rowId = editingRowId;
        cancelEdit();

        // a blank name is nothing to save: the draft is given up, and a role keeps the name it has
        if (!name || rowId === null) {
            return;
        }

        const saved = rowId === NEW_ROLE_ROW_ID
            ? Api.addRuleConfigurationRoles(currentContextId, name)
            : Api.updateRuleConfigurationRoles(currentContextId, {idRole: Number(rowId), name});

        saved.then(getData, handleError);
    }, [editingRowId, cancelEdit, currentContextId, getData]);

    const deleteRow = useCallback((row: RoleGridRow) => {
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: translate('popUp.deleteRoleTitle'),
            description: translate('roles.confirmDelete'),
            confirmButtonText: 'popUp.delete',
            cancelButtonText: 'popUp.cancel',
            severity: MODAL_SEVERITY.WARNING,
            handleAction: () => {
                toggleModal(null);
                // the rules and configuration built on the role go with it, which is what the
                // popup warned about
                Api.deleteRuleConfigurationRoles(currentContextId, row.roleId, true)
                    .then(getData, handleError);
            }
        });
    }, [toggleModal, translate, currentContextId, getData]);

    const nameChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        draftName.current = event.target.value;
    }, []);

    const nameKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KEY.ENTER) {
            saveRow();
        } else if (event.key === KEY.ESCAPE) {
            cancelEdit();
        }
    }, [saveRow, cancelEdit]);

    const rowData = useMemo(() => buildRowData(roles, showNewRow), [roles, showNewRow]);

    // the draft is appended, so on a list longer than the grid the Add button would otherwise
    // look like it did nothing
    useEffect(() => {
        if (showNewRow) {
            gridApi.current?.ensureIndexVisible(rowData.length - 1);
        }
    }, [showNewRow, rowData.length]);

    /**
     * Focuses the Edit button of a row, waiting for the row to be drawn again if it has to: the
     * cell still holds the editor's own buttons for a frame after the row leaves edit mode, and
     * the Edit button that replaces them is only there once ag-grid has drawn the cell again.
     */
    const returnFocus = useCallback((rowId: string, attempts = FOCUS_RENDER_ATTEMPTS) => {
        const edit = document.getElementById(editButtonId(rowId));
        if (edit) {
            edit.focus();
        } else if (attempts > 0) {
            requestAnimationFrame(() => returnFocus(rowId, attempts - 1));
        }
    }, []);

    /**
     * Hands keyboard focus back once an editor closes: to the Edit button of the row it closed
     * on, which is where the tab order stood when that row was opened - or to the Add button,
     * when the row that closed was the draft and so is no longer there to focus.
     */
    useEffect(() => {
        const rowId = returnFocusTo.current;
        if (editingRowId !== null || rowId === null) {
            return;
        }
        returnFocusTo.current = null;

        if (!gridApi.current?.getRowNode(rowId)) {
            document.getElementById(ADD_BUTTON_ID)?.focus();
            return;
        }
        returnFocus(rowId);
    }, [editingRowId, rowData, returnFocus]);

    /**
     * The tab order inside the grid: the headings that sort - the name alone - and then the
     * controls the rows carry, which are the Edit and Delete buttons of a row, and the name input
     * with the Save and Cancel buttons of the row being edited.
     *
     * What the grid and the browser would do between them is walk every heading, the actions one
     * that does not sort included, in a ring, and stop on each row before the controls it carries
     * - the row is a tab stop of the grid's own making (see AppGrid), and standing on one does
     * nothing. Every step below is one of those skipped: the last sortable heading hands focus to
     * the first control, and the last control of a row hands it to the first control of the row
     * under it. The last control of the last rendered row is left to the browser, which carries
     * focus out of the grid and on to the Add button below it - where it should go.
     */
    const onGridKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== KEY.TAB) {
            return;
        }

        const grid = event.currentTarget;
        const controls = Array.from(grid.querySelectorAll<HTMLElement>(ROW_CONTROLS));
        if (controls.length === 0) {
            return;
        }

        const headers = Array.from(grid.querySelectorAll<HTMLElement>(SORTABLE_HEADERS));
        const lastHeader = headers[headers.length - 1];
        const target = event.target as HTMLElement;

        const moveTo = (element: HTMLElement | undefined) => {
            if (!element) {
                return;
            }
            element.focus();
            event.preventDefault();
        };

        if (target === lastHeader && !event.shiftKey) {
            moveTo(controls[0]);
            return;
        }

        const index = controls.indexOf(target);
        if (index === -1) {
            return;
        }

        if (event.shiftKey) {
            // back off the first control, and the headings pick the walk up again
            moveTo(index === 0 ? lastHeader : controls[index - 1]);
        } else if (index < controls.length - 1) {
            moveTo(controls[index + 1]);
        }
    }, []);

    const columnDefs = useMemo<ColDef<RoleGridRow>[]>(() => {
        const nameLabel = translate('roles.approverRole');
        const editLabel = translate('workflows.edit');
        const deleteLabel = translate('workflows.delete');

        /**
         * Which keys the cell's own content owns rather than the grid.
         *
         * Tab always: the buttons and the input a cell holds are in the document's tab order, and
         * leaving Tab to the browser is what walks focus through them - the grid would jump a
         * whole cell instead. And every key in a row being edited, so the input keeps its caret
         * rather than having Enter, Escape and the arrows read as navigation.
         */
        const cellOwnsKey = (params: SuppressKeyboardEventParams<RoleGridRow>) =>
            params.event.key === KEY.TAB || params.data?.id === editingRowId;

        return [{
            colId: NAME_COL,
            field: 'name',
            headerName: nameLabel,
            // a narrowed column clips its heading; the tooltip is what spells it out again
            headerTooltip: nameLabel,
            flex: 1,
            minWidth: 160,
            // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked for
            // explicitly
            tooltipValueGetter: params => params.data?.name || undefined,
            // the endpoint hands the whole list back in the order it keeps it, and the third
            // click on the heading is what returns the rows to that order
            sortable: true,
            suppressHeaderKeyboardEvent: tabOutOfTheHeaderIsNotTheGrids,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<RoleGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (row.id !== editingRowId) {
                    return row.name;
                }
                return (
                    <TextInput id="roles-name-input"
                               className="ga-input__title new-roles__name-input"
                               defaultValue={row.name}
                               autoFocus={true}
                               ariaLabel={nameLabel}
                               dataCy="roles.name"
                               onChange={nameChanged}
                               onKeyDown={nameKeyDown}/>
                );
            }
        }, {
            colId: ACTIONS_COL,
            headerName: translate('roles.actions'),
            type: 'rightAligned',
            width: ACTIONS_WIDTH,
            minWidth: ACTIONS_WIDTH,
            maxWidth: ACTIONS_WIDTH,
            sortable: false,
            resizable: false,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<RoleGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }

                if (row.id === editingRowId) {
                    return (
                        <span className="new-roles__actions">
                            <Button type={ButtonType.SECONDARY}
                                    onClick={saveRow}
                                    text={translate('roles.save')}
                            />
                            <Button type={ButtonType.SECONDARY}
                                    onClick={cancelEdit}
                                    text={translate('roles.cancel')}
                            />
                        </span>
                    );
                }

                return (
                    <span className="new-roles__actions">
                        <Button id={editButtonId(row.id)}
                                type={ButtonType.GHOST}
                                iconOnly={true}
                                icon={<PencilIcon size={ICON_SIZE.MEDIUM}/>}
                                disabled={readOnly}
                                onClick={() => enterEditMode(row)}
                                ariaLabel={editLabel}
                                tooltip={{id: `roles-edit-tooltip-${row.id}`, content: () => editLabel}}
                        />
                        <Button type={ButtonType.GHOST}
                                iconOnly={true}
                                icon={<Trash2Icon size={ICON_SIZE.MEDIUM}/>}
                                disabled={readOnly}
                                onClick={() => deleteRow(row)}
                                ariaLabel={deleteLabel}
                                tooltip={{id: `roles-delete-tooltip-${row.id}`, content: () => deleteLabel}}
                        />
                    </span>
                );
            }
        }];
    }, [translate, readOnly, editingRowId, nameChanged, nameKeyDown, saveRow, cancelEdit, enterEditMode, deleteRow]);

    // a reload keeps the rows on screen; only the very first load is waited for
    const showGrid = loadingStatus !== LOADING_STATUS.LOADING || rowData.length > 0;

    return (
        // the outer wrapper is what ConfigurationWrapper used to render around every configuration page
        <div>
            <div className="w-100">
                <main className="page w-100 d-flex justify-content-center align-items-start">
                    <div className="py-16 mb-48 w-50 settings-page__container">
                        <h2 className="ps-4 mb-4 d-flex align-items-center">{translate("roles.header")}
                            <span className="ms-4"
                                  data-for={SHARED_TOOLTIP_ID}
                                  data-place={HEADING_INFO_TOOLTIP_PLACE}
                                  data-tip="roles-header-info-tooltip">
                                <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                            </span>
                            <Tooltip
                                id="roles-header-info-tooltip"
                                content={() => translate("roles.headerInfo")}
                            />
                        </h2>

                        <div className="bg-white radius-4 p-4 setting_collapsable-main new-roles__card">
                            {showGrid
                                ? <AppGrid<RoleGridRow>
                                    className="new-roles__grid"
                                    dataCy="roles.grid"
                                    containerRef={setGridElement}
                                    height={gridHeight}
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    getRowId={(params: GetRowIdParams<RoleGridRow>) => params.data.id}
                                    rowHeight={ROW_HEIGHT}
                                    postSortRows={keepDraftLast}
                                    onGridReady={onGridReady}
                                    onKeyDown={onGridKeyDown}
                                    localeText={{noRowsToShow: translate('roles.noMatches')}}
                                />
                                : <div className="new-roles__loading" ref={setGridElement}
                                       style={gridHeight ? {height: gridHeight} : undefined}>
                                    <Loader size={LoaderSize.Medium}/>
                                </div>
                            }

                            {!readOnly &&
                                <div className="d-flex justify-content-end mt-4">
                                    <Button id={ADD_BUTTON_ID}
                                            type={ButtonType.SECONDARY}
                                            onClick={addRole}
                                            disabled={showNewRow}
                                            text={translate("roles.add")}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

/**
 * Configuration -> Roles. The flag is read here rather than around the route, so the page itself
 * decides which of its two renderings it is.
 */
export const RolesWrapper = () => {
    const useAgGrid = launchDarkly.userClient?.variation(AG_GRID_IN_CONFIGURATION_FLAG, false) ?? false;

    return useAgGrid ? <NewRoles/> : <Roles/>;
};

export default RolesWrapper;
